import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getProjects, createProject, updateProject, deleteProject, getClients } from '@/lib/api';
import { toast } from 'sonner';

const STAGES = [
  { value: 'audit', label: 'Technischer Audit', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  { value: 'sprint', label: 'Creative Sprint', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  { value: 'launch', label: 'Soft Launch', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'scale', label: 'Scaling', color: 'bg-teal-50 text-teal-700 border-teal-200' },
  { value: 'retention', label: 'Retention Loop', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form, setForm] = useState({ client_id: '', name: '', stage: 'audit', owner: '', next_milestone: '', notes: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [prRes, clRes] = await Promise.all([getProjects(), getClients()]);
      setProjects(prRes.data);
      setClients(clRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editingProject) { await updateProject(editingProject.id, form); toast.success('Projekt aktualisiert'); }
      else { await createProject(form); toast.success('Projekt erstellt'); }
      setDialogOpen(false); setEditingProject(null); loadData();
    } catch (err) { toast.error('Fehler beim Speichern'); }
  };

  const handleStageChange = async (project, newStage) => {
    try {
      await updateProject(project.id, { stage: newStage });
      toast.success(`Phase geändert: ${STAGES.find(s => s.value === newStage)?.label}`);
      loadData();
    } catch (err) { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Projekt löschen?')) return;
    try { await deleteProject(id); toast.success('Gelöscht'); loadData(); } catch (err) { toast.error('Fehler'); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Unbekannt';

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Projekte (Workflow Board)" description="5-Stufen-Prozess: Audit → Sprint → Launch → Scale → Retention">
        <Button onClick={() => { setEditingProject(null); setForm({ client_id: clients[0]?.id || '', name: '', stage: 'audit', owner: '', next_milestone: '', notes: '' }); setDialogOpen(true); }} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="project-create-button">
          <Plus className="w-4 h-4 mr-2" /> Neues Projekt
        </Button>
      </PageHeader>

      {/* Stage indicators */}
      <motion.div variants={fadeIn} className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {STAGES.map((s, i) => (
          <div key={s.value} className="flex items-center gap-1 flex-shrink-0">
            <span className={`stage-badge text-xs font-medium px-3 py-1.5 rounded-full border ${s.color}`}>{s.label}</span>
            {i < STAGES.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300" />}
          </div>
        ))}
      </motion.div>

      {/* Board Columns */}
      <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {STAGES.map(stage => {
          const stageProjects = projects.filter(p => p.stage === stage.value);
          return (
            <div key={stage.value} className="min-h-[200px]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-xs font-semibold text-slate-600 uppercase tracking-wider">{stage.label}</h3>
                <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">{stageProjects.length}</span>
              </div>
              <div className="space-y-3">
                {stageProjects.map(p => (
                  <div key={p.id} className="kpi-card-hover rounded-xl border border-slate-200 bg-white p-4" data-testid={`project-card-${p.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="text-sm font-semibold text-slate-900 leading-tight">{p.name}</h4>
                      <div className="flex gap-0.5">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditingProject(p); setForm({ client_id: p.client_id, name: p.name, stage: p.stage, owner: p.owner, next_milestone: p.next_milestone, notes: p.notes || '' }); setDialogOpen(true); }}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => handleDelete(p.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{getClientName(p.client_id)}</p>
                    {p.owner && <p className="text-xs text-slate-400">Verantwortlich: {p.owner}</p>}
                    {p.next_milestone && <p className="text-xs text-teal-600 mt-1">{p.next_milestone}</p>}
                    {/* Move buttons */}
                    <div className="flex gap-1 mt-3">
                      {STAGES.map(s => s.value !== stage.value && (
                        <button key={s.value} onClick={() => handleStageChange(p, s.value)} className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${s.color} hover:opacity-80`}>
                          {s.label.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {stageProjects.length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-slate-200 p-4 text-center">
                    <p className="text-xs text-slate-400">Keine Projekte</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </motion.div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editingProject ? 'Projekt bearbeiten' : 'Neues Projekt'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Projektname" data-testid="project-form-name" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Kunde</label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}><SelectTrigger><SelectValue placeholder="Kunde" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Phase</label>
                <Select value={form.stage} onValueChange={v => setForm({...form, stage: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Verantwortlich</label><Input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})} placeholder="Name" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Nächster Meilenstein</label><Input value={form.next_milestone} onChange={e => setForm({...form, next_milestone: e.target.value})} placeholder="Meilenstein" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="project-form-save">{editingProject ? 'Aktualisieren' : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
